import React, { useState, useEffect, useRef } from 'react';
import styles from './TrendingCourses.module.css';
import { FaGraduationCap, FaStar, FaChartLine, FaCode, FaPaintBrush, FaCloud, FaMobileAlt, FaDatabase, FaCogs, FaShieldAlt } from 'react-icons/fa';

const trendingCourses = [
  {
    id: 1,
    title: 'Full Stack Web Development',
    subtitle: 'HTML, CSS, JavaScript, React.js, Node.js, SQL',
    students: '120,000+ Students',
    rating: '4.8',
    color: '#667eea',
    icon: <FaCode />
  },
  {
    id: 2,
    title: 'Java Programming Fundamentals',
    subtitle: 'OOPS, Arrays, Loops, Data Structures',
    students: '95,000+ Students',
    rating: '4.9',
    color: '#f093fb',
    icon: <FaChartLine />
  },
  {
    id: 3,
    title: 'Python Programming Fundamentals',
    subtitle: 'Data Structures, OOPS, Functions, Modules',
    students: '80,000+ Students',
    rating: '4.7',
    color: '#4facfe',
    icon: <FaPaintBrush />
  },
  {
    id: 4,
    title: 'React JS Essentials',
    subtitle: 'Fundamentals, Hooks, API Integration & Deployment',
    students: '75,000+ Students',
    rating: '4.6',
    color: '#43e97b',
    icon: <FaCloud />
  },
  {
    id: 5,
    title: 'Node.js Backend Development',
    subtitle: 'Modules, File System, Express.js',
    students: '110,000+ Students',
    rating: '4.8',
    color: '#667eea',
    icon: <FaMobileAlt />
  },
  {
    id: 6,
    title: 'SQL Database Fundamentals',
    subtitle: 'Data Retrieval, Data Manipulation & Joins',
    students: '60,000+ Students',
    rating: '4.5',
    color: '#f093fb',
    icon: <FaDatabase />
  },
  {
    id: 7,
    title: 'Software Testing',
    subtitle: 'Manual Concepts, UI Test Automation, API Testing',
    students: '90,000+ Students', 
    rating: '4.9',
    color: '#4facfe',
    icon: <FaShieldAlt />
  },
  {
    id: 8,
    title: 'Selenium Test Automation',
    subtitle: 'Fundamentals, Frameworks, Grid & CI/CD',
    students: '70,000+ Students',
    rating: '4.8',
    color: '#43e97b',
    icon: <FaCogs />
  },
  {
    id: 9,
    title: 'Playwright Test Automation',
    subtitle: 'Features, Parallelism, Framework Design',
    students: '65,000+ Students',
    rating: '4.8',
    color: '#667eea',
    icon: <FaCogs />
  },
  {
    id: 10,
    title: 'API Automation Testing',
    subtitle: 'Manual and Automated API Testing',
    students: '55,000+ Students',
    rating: '4.7',
    color: '#f093fb',
    icon: <FaCogs />
  },
  {
    id: 11,
    title: 'ETL & Data Warehousing Testing',
    subtitle: 'ETL Testing Lifecycle, Tools & Techniques',
    students: '45,000+ Students',
    rating: '4.6',
    color: '#4facfe',
    icon: <FaCogs />
  },
  {
    id: 12,
    title: 'JMeter Performance Testing',
    subtitle: 'Fundamentals, Test Plan, Reporting',
    students: '50,000+ Students',
    rating: '4.8',
    color: '#43e97b',
    icon: <FaCogs />
  }
];

const TrendingCourses = () => {
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Auto-scroll functionality
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollInterval;
    if (!isHovered) {
      scrollInterval = setInterval(() => {
        const scrollPosition = scrollContainer.scrollLeft;
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        
        if (scrollPosition >= maxScroll) {
          // Reset to beginning for continuous loop
          scrollContainer.scrollLeft = 0;
        } else {
          scrollContainer.scrollLeft += 1;
        }
      }, 30);
    }

    return () => clearInterval(scrollInterval);
  }, [isHovered]);

  // Check scroll position for indicators
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < maxScroll);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToDirection = (direction) => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const cardWidth = 280; // Card width + gap
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    
    scrollContainer.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleCourseClick = (course) => {
    console.log('Course clicked:', course.title);
    // Add your navigation logic here
    // For example: navigate(`/course/${course.id}`)
  };

  return (
    <div 
      className={styles.trendingContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.cardsWrapper} ref={scrollRef}>
        {/* Original courses */}
        {trendingCourses.map(course => (
          <div 
            key={course.id} 
            className={styles.courseCard} 
            style={{ '--card-color': course.color }}
            onClick={() => handleCourseClick(course)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.icon}>{course.icon}</div>
              <span className={styles.rating}>
                <FaStar size={12} />
                {course.rating}
              </span>
            </div>
            <h3 className={styles.title}>{course.title}</h3>
            <p className={styles.subtitle}>{course.subtitle}</p>
            <div className={styles.cardFooter}>
              <p className={styles.students}>{course.students}</p>
              <button 
                className={styles.learnMoreBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCourseClick(course);
                }}
              >
                Enroll Now
              </button>
            </div>
          </div>
        ))}
        
        {/* Duplicate courses for seamless loop */}
        {trendingCourses.slice(0, 6).map(course => (
          <div 
            key={`dup-${course.id}`} 
            className={styles.courseCard} 
            style={{ '--card-color': course.color }}
            onClick={() => handleCourseClick(course)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.icon}>{course.icon}</div>
              <span className={styles.rating}>
                <FaStar size={12} />
                {course.rating}
              </span>
            </div>
            <h3 className={styles.title}>{course.title}</h3>
            <p className={styles.subtitle}>{course.subtitle}</p>
            <div className={styles.cardFooter}>
              <p className={styles.students}>{course.students}</p>
              <button 
                className={styles.learnMoreBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCourseClick(course);
                }}
              >
                Enroll Now
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendingCourses;